import { useState, useCallback, useRef } from 'react';

/**
 * Reusable hook for "unsaved changes" guard.
 *
 * Usage:
 *   const { markDirty, markClean, requestClose, confirmProps } = useUnsavedChanges();
 *
 *   - Call markDirty() on every user change.
 *   - Call markClean() after a successful save / on dialog open.
 *   - Replace your close handler with: () => requestClose(onClose)
 *   - Spread {...confirmProps} onto an <UnsavedChangesAlert> or your own AlertDialog.
 */
export function useUnsavedChanges() {
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const pendingCallback = useRef(null);

  const markDirty = useCallback(() => setIsDirty(true), []);
  const markClean = useCallback(() => setIsDirty(false), []);

  /** Call instead of closing directly. Shows confirm if dirty, otherwise closes immediately. */
  const requestClose = useCallback((onClose) => {
    if (!isDirty) {
      onClose();
      return;
    }
    pendingCallback.current = onClose;
    setShowConfirm(true);
  }, [isDirty]);

  /** User confirmed — discard changes and close. */
  const confirmDiscard = useCallback(() => {
    setShowConfirm(false);
    setIsDirty(false);
    if (pendingCallback.current) {
      pendingCallback.current();
      pendingCallback.current = null;
    }
  }, []);

  /** User cancelled — stay on the page. */
  const cancelDiscard = useCallback(() => {
    setShowConfirm(false);
    pendingCallback.current = null;
  }, []);

  return {
    isDirty,
    markDirty,
    markClean,
    requestClose,
    /** Spread these props onto <UnsavedChangesAlert> */
    confirmProps: { open: showConfirm, onConfirm: confirmDiscard, onCancel: cancelDiscard },
  };
}
